import DotsHorizontal from "@uui/DotsHorizontal";
import { every, filter, flatMap } from "lodash/fp";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import type { IntegrationAccessSetting } from "@app/common/types";
import type {
  EnterpriseWorkspaceSettings,
  EnterpriseWorkspaceUserIntegrationCounts,
} from "@app/common/workspaces/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import IconButton from "@rui/IconButton";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Row } from "@rui/Stack";

import type { IntegrationSettingCategory } from "./constants";
import IntegrationCategoryRow from "./IntegrationCategoryRow";
import { CategoryContainer } from "./styles";

const IntegrationCategory = ({
  category,
  disabled,
  integrationCounts,
  onToggle,
  settings,
}: {
  category: IntegrationSettingCategory;
  disabled: boolean;
  integrationCounts: EnterpriseWorkspaceUserIntegrationCounts;
  onToggle: (opts: {
    settings: IntegrationAccessSetting[];
    enable: boolean;
  }) => void;
  settings: EnterpriseWorkspaceSettings;
}) => {
  const { anchorElement, onClick, onClose, open } = useAnchorElement();

  const allCategorySettings = flatMap("settings", category.settings);

  const handleEnableAll = () => {
    onClose();
    onToggle({ settings: allCategorySettings, enable: true });
  };

  const handleDisableAll = () => {
    onClose();
    onToggle({ settings: allCategorySettings, enable: false });
  };

  const numEnabled = filter(
    (setting) => every((s) => settings[s], setting.settings),
    category.settings
  ).length;

  return (
    <>
      <CategoryContainer spacing={1}>
        <Row variant="apart" spacing={3} pb={1}>
          <SettingsTitle title={category.title} />
          <IconButton onClick={onClick} size="small" sx={{ mr: 0.5 }}>
            <DotsHorizontal />
          </IconButton>
        </Row>
        {category.settings.map((setting) => {
          if (setting.disabled?.()) return null;
          return (
            <IntegrationCategoryRow
              key={setting.provider}
              disabled={disabled}
              numConnected={integrationCounts[setting.provider] ?? 0}
              onToggle={onToggle}
              setting={setting}
              settings={settings}
            />
          );
        })}
      </CategoryContainer>

      <Menu open={open} onClose={onClose} anchorEl={anchorElement}>
        {numEnabled < category.settings.length && (
          <MenuItem onClick={handleEnableAll}>Enable all</MenuItem>
        )}
        {numEnabled > 0 && (
          <MenuItem onClick={handleDisableAll}>Disable all</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default IntegrationCategory;
